import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { Button as MuiButton, InputAdornment, TextField } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { debounce } from 'lodash';

import AddOrEditPersonnelModal from 'components/pages/study/StudyDetails/SiteDetails/AddOrEditPersonnelModal/AddOrEditPersonnelModal';

import { UserApiApi } from '../../../../../../api';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../common/general/Button';
import ButtonGroup from '../../../../../../common/general/ButtonGroup';
import { emailRegexp } from '../../../../../../services/email';
import { NS_COMMON, NSPS_SITE } from '../../../../../../services/i18n/namespaces';

import './SearchPersonnelModal.scss';

const debounceSearchString = debounce(cb => cb(), 500);

export default function SearchPersonnelModal({ ssuId, studyId, studyName, isGroupAssignStudy, siteName, modalBox }) {
  const { t } = useTranslation();
  const [personnels, setPersonnels] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');

  const openAddOrEditPersonnelModal = useCallback(
    personnelId => {
      ModalBoxes.open({
        component: (
          <AddOrEditPersonnelModal
            ssuId={ssuId}
            studyId={studyId}
            studyName={studyName}
            isGroupAssignStudy={isGroupAssignStudy}
            siteName={siteName}
            personnelId={personnelId}
            email={searchEmail.trim()}
          />
        ),
        title: t('addPersonnel', { ns: NSPS_SITE })
      });
    },
    [isGroupAssignStudy, searchEmail, siteName, ssuId, studyId, studyName, t]
  );

  const columns = useMemo(
    () => [
      {
        field: 'email',
        headerName: t('searchPersonnel.table.email', { ns: NSPS_SITE }),
        flex: 1.5
      },
      {
        field: 'firstName',
        headerName: t('searchPersonnel.table.firstName', { ns: NSPS_SITE }),
        flex: 1
      },
      {
        field: 'lastName',
        headerName: t('searchPersonnel.table.lastName', { ns: NSPS_SITE }),
        flex: 1
      },
      {
        field: 'actions',
        headerName: t('addOrEditPersonnel.currentRoles.actions', { ns: NSPS_SITE }),
        flex: 1,
        renderCell({ row }) {
          return (
            <MuiButton
              variant="text"
              sx={{ textTransform: 'none' }}
              onClick={() => openAddOrEditPersonnelModal(row.id)}
            >
              {t('edit', { ns: NS_COMMON })}
            </MuiButton>
          );
        }
      }
    ],
    [t, openAddOrEditPersonnelModal]
  );

  const addUserButtonDisabled = useMemo(() => {
    return personnels?.length > 0 || !searchEmail || !emailRegexp.test(searchEmail);
  }, [searchEmail, personnels]);

  useEffect(() => {
    debounceSearchString(function() {
      if (searchEmail && searchEmail.trim().length > 0) {
        UserApiApi.searchPersonnels(ssuId, { email: searchEmail.trim() }).then(({ data }) => setPersonnels(data));
      } else {
        setPersonnels([]);
      }
    });
  }, [ssuId, searchEmail, setPersonnels]);

  const onSearchEmailChange = e => {
    setSearchEmail(e.target.value);
  };

  return (
    <>
      <ModalBoxes.Body>
        <TextField
          id="email"
          type="email"
          name="email"
          onChange={onSearchEmailChange}
          placeholder="example@gmail.com"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1 }}>
                  <SearchIcon />
                  <span>{t('searchPersonnel.searchEmail', { ns: NSPS_SITE })}</span>
                </InputAdornment>
              )
            }
          }}
          size="small"
        />
        <div className="search-result-container">
          {personnels && personnels.length > 0 ? (
            <DataGridPremium
              rows={personnels}
              columns={columns}
              getRowId={getRowId}
              disableRowSelectionOnClick
              disableRowGrouping
              disableAggregation
              disablePivoting
              pagination
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10
                  }
                }
              }}
            />
          ) : (
            <span className="search-result-empty">{t('searchPersonnel.noUserFound', { ns: NSPS_SITE })}</span>
          )}
        </div>
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <ButtonGroup>
          <Button disabled={addUserButtonDisabled} onClick={() => openAddOrEditPersonnelModal()}>
            {t('searchPersonnel.createNewUser', { ns: NSPS_SITE })}
          </Button>
          <Button onClick={modalBox.close}>{t('close', { ns: NS_COMMON })}</Button>
        </ButtonGroup>
      </ModalBoxes.Footer>
    </>
  );
}

function getRowId({ id }) {
  return id;
}

SearchPersonnelModal.className = 'search-personnel-modal';
SearchPersonnelModal.size = 'w800';
